import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogIn, Mail, Lock, BookOpen, GalleryVerticalEnd } from "lucide-react";
import { SiGoogle } from "react-icons/si";
const GoogleIcon = SiGoogle as unknown as React.ComponentType<{ className?: string }>;

const Login: React.FC = () => {
  const { user, login, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const header = document.querySelector("header");
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => {
      if (!header) return;
      if (mq.matches) header.classList.add("hidden");
      else header.classList.remove("hidden");
    };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      if (header) header.classList.remove("hidden");
    };
  }, []);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const success = await login(formData.email, formData.password);
    if (success) {
      // Navigation will be handled by the Navigate component above
    }

    setLoading(false);
  };

  return (
    <>
      <div className="lg:hidden grid min-h-svh">
        <div className="flex flex-col gap-4 p-6 md:p-10">
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-6">
              <img src="/LogoBeatPost.png" alt="Beatpost" style={{ width: "93px", height: "85px" }} />
              <div className="w-full max-w-xs">
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="flex flex-col items-center gap-1 text-center">
                    <h1 className="text-2xl font-bold text-newspaper-900">Login to your account</h1>
                    <p className="text-sm text-newspaper-600">Enter your email below to login to your account</p>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="beat-input rounded-lg"
                      placeholder="m@example.com"
                      required
                    />
                  </div>
                  <div>
                    <div className="flex items-center">
                      <label htmlFor="password" className="block text-sm font-medium text-newspaper-700">
                        Password
                      </label>
                      <Link
                        to="/forgot-password"
                        className="ml-auto text-sm underline-offset-4 hover:underline text-newspaper-900"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="beat-input rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
                    >
                      {loading ? (
                        <div className="beat-spinner"></div>
                      ) : (
                        <>
                          <LogIn className="w-4 h-4" />
                          <span>Login</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-newspaper-300"></div>
                    <span className="text-xs text-newspaper-600">Or continue with</span>
                    <div className="flex-1 border-t border-newspaper-300"></div>
                  </div>
                  <div>
                    {/* Login with Google (mobile) */}
                    <button
                      type="button"
                      onClick={() => loginWithGoogle()}
                      className="LoginWithGoogle w-full beat-button-secondary flex items-center justify-center space-x-2 py-3 rounded-lg"
                    >
                      <GoogleIcon className="w-4 h-4" />
                      <span>Login with Google</span>
                    </button>
                    <p className="text-center text-newspaper-600 mt-3">
                      Don't have an account?{" "}
                      <Link to="/register" className="underline underline-offset-4 text-newspaper-900">
                        Sign up
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="beat-container max-w-md mx-auto">
          <div className="beat-card p-8 rounded-lg">
            <div className="text-center mb-8">
              <div className="flex justify-center items-center mb-4">
                <BookOpen className="w-12 h-12 text-newspaper-900" />
              </div>
              <h1 className="text-2xl font-bold text-newspaper-900 mb-2">Sign In</h1>
              <p className="text-newspaper-600">Welcome back to Beatpost</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="beat-input pl-10 rounded-lg"
                    placeholder="your@email.com"
                    required
                  />
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="beat-input pl-10 rounded-lg"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
              >
                {loading ? (
                  <div className="beat-spinner"></div>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-newspaper-600">
                Don't have an account?{" "}
                <Link to="/register" className="text-newspaper-900 font-medium hover:underline">
                  Register here
                </Link>
              </p>
            </div>
            <div className="mt-8 p-4 bg-newspaper-100 border border-newspaper-200 rounded-lg">
              <p className="text-xs text-newspaper-600">
                You can create a new account or use your credentials once you register a user.
              </p>
            </div>
            <div className="mt-6 text-center">
              <p className="text-newspaper-600">
                Forgot your password?{" "}
                <Link to="/forgot-password" className="text-newspaper-900 font-medium hover:underline">
                  Reset it here
                </Link>
              </p>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 border-t border-newspaper-300"></div>
              <span className="text-xs text-newspaper-600">Or continue with</span>
              <div className="flex-1 border-t border-newspaper-300"></div>
            </div>
            {/* Login with Google Button */}
            <div className="mt-3 LoginWithGoogle">
              <button
                type="button"
                onClick={() => loginWithGoogle()}
                className="w-full beat-button-secondary flex items-center justify-center space-x-2 py-3 rounded-lg"
              >
                <GoogleIcon className="w-4 h-4" />
                <span>Login with Google</span>
              </button>
            </div>
            <div className="mt-6 text-center">
              <div className="beat-quote text-sm">
                "The only people for me are the mad ones, the ones who are mad to live, mad to talk, mad to be saved..."
              </div>
              <p className="text-xs text-newspaper-500 mt-2">— Jack Kerouac</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
